import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useEffect, useState } from "react";

interface CityResult {
  id: string;
  name: string;
  type: 'kommun' | 'stad';
}

type SwedenCity = Database['public']['Tables']['sweden_cities']['Row'];

export const useCitySearch = (searchTerm: string) => {
  const [results, setResults] = useState<CityResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const searchCities = async () => {
      console.log('searchCities called with term:', searchTerm);
      if (!searchTerm || searchTerm.length < 2) {
        console.log('Search term too short, clearing results');
        setResults([]);
        return;
      }

      console.log('Starting search for:', searchTerm);
      setIsLoading(true);
      
      try {
        // Only search for cities (Tätortsbeteckning) - using unquoted column name consistently
        const { data, error } = await supabase
          .from('sweden_cities')
          .select('Tätortsbeteckning')
          .ilike('Tätortsbeteckning', `${searchTerm}%`)
          .order("Befolkning 2023-12-31", {ascending: false})
          .limit(10);

        console.log('Supabase query executed:', {
          searchTerm,
          dataCount: data?.length || 0,
          firstResult: data?.[0],
          error
        });

        console.log('Query results:', { data, error });

        if (error) throw error;

        const cityResults: CityResult[] = [];
        const seenStad = new Set<string>();

        data?.forEach((city: any) => {
          // Add stad results only - no need to filter again since database already filters
          if (city["Tätortsbeteckning"] && !seenStad.has(city["Tätortsbeteckning"])) {
            cityResults.push({
              id: `stad-${city["Tätortsbeteckning"]}`,
              name: city["Tätortsbeteckning"],
              type: 'stad'
            });
            seenStad.add(city["Tätortsbeteckning"]);
          }
        });

        console.log('Final results:', cityResults);
        setResults(cityResults);
      } catch (error) {
        console.error('Error searching cities:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(searchCities, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  return { results, isLoading };
};